import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Products } from '../../enums/header-products.enum';
import { ProductCategory } from '../../models/auth-state.model';
import { AuthStateService } from '../../services/auth-state.service';
import { BrandService } from '../../services/brand.service';
import { LoginService } from '../../services/login.service';
import { ProductsService } from '../../services/products.service';

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false,
})
export class HeaderComponent implements OnInit {
  products: string[] = Object.values(Products);
  categories: ProductCategory[] = [];
  displayProducts: Array<{ name: string; disabled: boolean }> = [];
  languages!: string[];
  selectedLang!: string;
  defaultImage: boolean = false;
  username: string | null = '';
  email: string | null = '';
  selectedProduct: string = 'DRYERS';
  isProfileMenuOpen = false;
  availableLanguages = [
    { code: 'en', name: 'English' },
    { code: 'de', name: 'Deutsch' },
    { code: 'fr', name: 'Français' },
    { code: 'it', name: 'Italiano' },
    { code: 'es', name: 'Español' },
    { code: 'pl', name: 'Polish' },
  ];
  constructor(
    private translate: TranslateService,
    private router: Router,
    private login: LoginService,
    private snackBar: MatSnackBar,
    private productService: ProductsService,
    private brandService: BrandService,
    private authStateService: AuthStateService,
  ) {
    translate.addLangs(this.availableLanguages.map((lang) => lang.code));
    translate.setDefaultLang('en');
  }

  ngOnInit() {
    this.languages = [...this.translate.getLangs()];
    this.selectedLang = localStorage.getItem('language') || 'en';
    this.translate.use(this.selectedLang);
    // Load display name and email from every available source so the header is
    // populated for both JWT and SSO sessions, and survives page refreshes.
    const user = this.login.getUser();
    const authState = this.authStateService.load();
    this.username = user?.fullName || user?.username || authState?.username || '';
    this.email =
      user?.email || this.resolveEmailFromUsername(user?.username || '') || this.resolveEmailFromUsername(authState?.username || '');
    this.categories = this.authStateService.categories;
    this.displayProducts = this.buildDisplayProducts(this.categories);
    if (!this.authStateService.activeCategoryId && this.authStateService.defaultCategoryId) {
      this.authStateService.updateActiveCategory(this.authStateService.defaultCategoryId);
    }
    this.syncSelectedProduct();
  }

  get logoSrc(): string {
    const brand = this.brandService.current;
    return brand.navLogoSrc ?? brand.logoSrc;
  }

  get brandDisplayName(): string {
    const displayNames: Record<string, string> = {
      compair: 'CompAir',
      nash: 'Nash',
    };
    return displayNames[this.brandService.current.id] ?? '';
  }

  get supportEmail(): string {
    return this.brandService.current.supportEmail;
  }

  get mailtoHref(): string {
    const brand = this.brandService.current;
    const subjectBase = '[Airtreatment] Support Request';
    const subject = encodeURIComponent(`${subjectBase}${this.selectedProduct ? ' - ' + this.selectedProduct : ''}`);

    const userLine = this.username ? `User: ${this.username}${this.email ? ' (' + this.email + ')' : ''}` : 'User: (not signed in)';

    const langLine = `Language: ${this.selectedLang || 'en'}`;
    const urlLine = `URL: ${brand.publicUrl ?? this.router.url}`;
    const brandLine = `Brand: ${brand.id}`;

    const describeIssue = this.translate.instant('HEADER.DESCRIBE_ISSUE');

    const body = encodeURIComponent(`${userLine}\n${langLine}\n${urlLine}\n${brandLine}\n\n${describeIssue}\n`);

    return `mailto:${brand.supportEmail}?subject=${subject}&body=${body}`;
  }

  switchLanguage(lang: string): void {
    localStorage.setItem('language', lang);
    this.selectedLang = lang;
    this.translate.use(lang);
  }

  selectProduct(product: string, disabled: boolean): void {
    if (disabled) {
      this.snackBar.open(this.translate.instant('HEADER.COMING_SOON'), 'Close', {
        duration: 3000,
        panelClass: ['snackbar-info'],
      });
      return;
    }
    this.selectedProduct = product;
    const matchedCategory = this.categories.find((category) => category.categoryName.toUpperCase() === product);
    if (matchedCategory) {
      this.authStateService.updateActiveCategory(matchedCategory.categoryId);
    }
    this.router.navigate(['/home']);
  }

  isCategoryActive(category: ProductCategory): boolean {
    return this.authStateService.activeCategoryId === category.categoryId;
  }

  isProductActive(product: string): boolean {
    return this.selectedProduct === product;
  }

  toggleProfile(): void {
    this.isProfileMenuOpen = !this.isProfileMenuOpen;
  }

  toggleSidenav(): void {
    this.productService.toggleSidenav();
  }

  logout(): void {
    const token = localStorage.getItem('token') ?? '';
    this.login.logout(token).then(() => {
      this.translate.get('USER.LOGOUT_SUCCESS').subscribe((res: string) => {
        this.snackBar.open(res, 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      });
    });
  }

  private syncSelectedProduct(): void {
    const activeCategoryId = this.authStateService.activeCategoryId || this.authStateService.defaultCategoryId;
    const activeCategory = this.categories.find((category) => category.categoryId === activeCategoryId);
    this.selectedProduct = activeCategory?.categoryName?.toUpperCase() || 'DRYERS';
  }

  private buildDisplayProducts(apiCategories: ProductCategory[]): Array<{ name: string; disabled: boolean }> {
    const apiProductNames = new Set(apiCategories.map((category) => category.categoryName.toUpperCase()));
    const knownProducts = ['DRYERS', 'DRYERS + FILTERS', 'COMPRESSED AIR SOLUTION', 'NITROGEN GENERATOR'];
    apiProductNames.forEach((name) => {
      if (!knownProducts.includes(name)) {
        knownProducts.push(name);
      }
    });
    return knownProducts.map((name) => ({ name, disabled: name !== Products.Dryers }));
  }

  private resolveEmailFromUsername(username: string): string {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(username) ? username : '';
  }
}
